export default function NFLGamePanel({ game }) {
  const sit = game.situation || {};
  const drives = game.drives || {};
  const isLive = game.statusState === "in";
  const away = game.awayTeam || {};
  const home = game.homeTeam || {};

  const yardLine = sit.yardLine || 0;
  const hasPossession = sit.down && sit.distance;
  const possTeamId = sit.possession || "";
  const isHomePoss = possTeamId === home.id;
  // Ball position: convert yard line to percentage across the 100-yard field
  const ballPct = Math.max(5, Math.min(95, yardLine));
  // First down line
  const firstDownYard = yardLine + (sit.distance || 0);
  const firstDownPct = Math.max(5, Math.min(95, firstDownYard));

  return (
    <div className="space-y-4" data-testid="nfl-panel">
      {/* Score by quarter */}
      <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden" data-testid="nfl-scoreboard">
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr className="border-b border-white/10 text-white/40 uppercase tracking-wider">
                <th className="text-left px-3 py-2 w-28">Team</th>
                {["1", "2", "3", "4", "OT"].slice(0, Math.max(4, (home.linescores || []).length)).map((q) => (
                  <th key={q} className="text-center px-3 py-2 w-10">{q}</th>
                ))}
                <th className="text-center px-3 py-2 w-12 bg-white/5 font-bold">T</th>
              </tr>
            </thead>
            <tbody>
              <tr className="border-b border-white/5">
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {away.logo && <img src={away.logo} alt="" className="w-5 h-5 object-contain" />}
                    <span className={`font-semibold ${away.winner ? "text-white" : "text-white/70"}`}>{away.abbreviation}</span>
                    {!isHomePoss && hasPossession && isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" title="Possession" />}
                  </div>
                </td>
                {(away.linescores || []).map((s, i) => (
                  <td key={i} className="text-center px-3 py-2.5 score-font text-white/60">{s}</td>
                ))}
                {(away.linescores || []).length < 4 && [...Array(4 - (away.linescores || []).length)].map((_, i) => (
                  <td key={`e${i}`} className="text-center px-3 py-2.5 score-font text-white/20">-</td>
                ))}
                <td className="text-center px-3 py-2.5 score-font font-bold text-white bg-white/5">{away.score}</td>
              </tr>
              <tr>
                <td className="px-3 py-2.5">
                  <div className="flex items-center gap-2">
                    {home.logo && <img src={home.logo} alt="" className="w-5 h-5 object-contain" />}
                    <span className={`font-semibold ${home.winner ? "text-white" : "text-white/70"}`}>{home.abbreviation}</span>
                    {isHomePoss && hasPossession && isLive && <span className="w-1.5 h-1.5 rounded-full bg-[#007AFF]" title="Possession" />}
                  </div>
                </td>
                {(home.linescores || []).map((s, i) => (
                  <td key={i} className="text-center px-3 py-2.5 score-font text-white/60">{s}</td>
                ))}
                {(home.linescores || []).length < 4 && [...Array(4 - (home.linescores || []).length)].map((_, i) => (
                  <td key={`e${i}`} className="text-center px-3 py-2.5 score-font text-white/20">-</td>
                ))}
                <td className="text-center px-3 py-2.5 score-font font-bold text-white bg-white/5">{home.score}</td>
              </tr>
            </tbody>
          </table>
        </div>
        {/* Timeouts */}
        {isLive && (sit.awayTimeouts != null || sit.homeTimeouts != null) && (
          <div className="flex items-center justify-between px-4 py-1.5 border-t border-white/5 text-[10px] text-white/30">
            <span>{away.abbreviation} TO: {[...Array(sit.awayTimeouts || 0)].map((_, i) => <span key={i} className="inline-block w-2 h-2 rounded-full bg-[#FF9500] mx-0.5" />)}</span>
            <span>{home.abbreviation} TO: {[...Array(sit.homeTimeouts || 0)].map((_, i) => <span key={i} className="inline-block w-2 h-2 rounded-full bg-[#FF9500] mx-0.5" />)}</span>
          </div>
        )}
      </div>

      {/* Football Field with ball position */}
      {isLive && hasPossession && (
        <div className="bg-[#141414] border border-white/10 rounded-xl overflow-hidden p-4" data-testid="football-field">
          {/* Down & Distance */}
          <div className="flex items-center justify-between mb-3">
            <span className="text-xs font-bold text-[#007AFF] tracking-wider uppercase">{sit.downDistanceText}</span>
            {sit.isRedZone && <span className="text-[10px] tracking-widest uppercase font-bold text-[#FF3B30] bg-[#FF3B30]/10 px-2 py-0.5 rounded">Red Zone</span>}
          </div>

          {/* Field visualization */}
          <div className="relative w-full h-16 rounded-lg overflow-hidden border border-white/10" style={{ background: "linear-gradient(to right, #1a472a, #2d6b3f, #1a472a)" }}>
            {/* Yard line markers */}
            {[10, 20, 30, 40, 50, 60, 70, 80, 90].map((yd) => (
              <div key={yd} className="absolute top-0 bottom-0 w-px bg-white/15" style={{ left: `${yd}%` }}>
                <span className="absolute -top-0.5 left-1/2 -translate-x-1/2 text-[7px] text-white/25 font-bold">
                  {yd <= 50 ? yd : 100 - yd}
                </span>
              </div>
            ))}
            {/* End zones */}
            <div className="absolute left-0 top-0 bottom-0 w-[5%] bg-[#007AFF]/20 flex items-center justify-center">
              <span className="text-[7px] text-white/30 font-bold rotate-90">{away.abbreviation}</span>
            </div>
            <div className="absolute right-0 top-0 bottom-0 w-[5%] bg-[#FF3B30]/20 flex items-center justify-center">
              <span className="text-[7px] text-white/30 font-bold rotate-90">{home.abbreviation}</span>
            </div>
            {/* First down line */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-[#FF9500]/80 z-10" style={{ left: `${firstDownPct}%` }} />
            {/* Ball position */}
            <div className="absolute top-1/2 -translate-y-1/2 z-20" style={{ left: `${ballPct}%`, transform: `translate(-50%, -50%)` }}>
              <div className="relative">
                <div className="w-5 h-3 rounded-full bg-[#8B4513] border border-[#5C2D0A] shadow-lg" />
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="w-2.5 h-px bg-white/50" />
                </div>
              </div>
            </div>
            {/* Line of scrimmage */}
            <div className="absolute top-0 bottom-0 w-0.5 bg-[#007AFF]/80 z-10" style={{ left: `${ballPct}%` }} />
          </div>

          {/* Field legend */}
          <div className="flex items-center gap-4 mt-2 text-[9px] text-white/30">
            <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#007AFF]" /> Line of scrimmage</span>
            <span className="flex items-center gap-1"><span className="w-2 h-0.5 bg-[#FF9500]" /> First down</span>
            {sit.isRedZone && <span className="text-[#FF3B30]">Red Zone</span>}
          </div>
        </div>
      )}

      {/* Drive info */}
      {drives.current && (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="drive-info">
          <p className="text-[10px] tracking-widest uppercase text-white/40 mb-2">Current Drive</p>
          <div className="flex items-center gap-4 text-xs">
            <span className="text-white/70">{drives.current.description}</span>
            <span className="text-white/40">{drives.current.plays} plays</span>
            <span className="text-white/40">{drives.current.yards} yds</span>
            <span className="text-white/40">{drives.current.timeElapsed}</span>
          </div>
        </div>
      )}

      {/* Last play */}
      {sit.lastPlayText && (
        <div className="bg-[#141414] border border-white/10 rounded-xl p-4" data-testid="last-play">
          <p className="text-[10px] tracking-widest uppercase text-white/40 mb-1">Last Play</p>
          <p className="text-xs text-white/70 leading-relaxed">{sit.lastPlayText}</p>
        </div>
      )}
    </div>
  );
}
